import { ConfirmDialog } from 'components/confirm-dialog/confirm-dialog'
import {
	ReactNode,
	useCallback,
	useEffect,
	useLayoutEffect,
	useMemo,
	useRef,
	useState
} from 'react'
import { createPortal } from 'react-dom'
import {
	DotMatrix,
	AppButton,
	Avatar,
	Banner,
	CardFilter,
	Checkbox,
	IconButton,
	Input,
	ListItem,
	MenuList,
	Pagination,
	Tab,
	TabList,
	TabPanel,
	Table,
	TableBody,
	TableCell,
	TableCellText,
	TableHead,
	TableHeader,
	TableRow,
	Tabs,
	Tooltip
} from '@clickguard/design-system'
import ArrowDown from '@untitled-ui/icons-react/build/esm/ArrowDown'
import ArrowUp from '@untitled-ui/icons-react/build/esm/ArrowUp'
import CalendarPlus02 from '@untitled-ui/icons-react/build/esm/CalendarPlus02'
import ChevronRight from '@untitled-ui/icons-react/build/esm/ChevronRight'
import ChevronSelectorVertical from '@untitled-ui/icons-react/build/esm/ChevronSelectorVertical'
import Clock from '@untitled-ui/icons-react/build/esm/Clock'
import DotsVertical from '@untitled-ui/icons-react/build/esm/DotsVertical'
import Download01 from '@untitled-ui/icons-react/build/esm/Download01'
import Trash01 from '@untitled-ui/icons-react/build/esm/Trash01'
import { toast } from 'react-toastify'
import FileSearch02 from '@untitled-ui/icons-react/build/esm/FileSearch02'
import FilterLines from '@untitled-ui/icons-react/build/esm/FilterLines'
import Plus from '@untitled-ui/icons-react/build/esm/Plus'
import RefreshCw02 from '@untitled-ui/icons-react/build/esm/RefreshCw02'
import Rows01 from '@untitled-ui/icons-react/build/esm/Rows01'
import SearchLg from '@untitled-ui/icons-react/build/esm/SearchLg'
import User01 from '@untitled-ui/icons-react/build/esm/User01'
import XClose from '@untitled-ui/icons-react/build/esm/XClose'
import { useParams } from 'react-router-dom'
import { PageLayout } from 'components/page-layout'
import { useAssetStore, useUserStore } from 'store'
import { ScheduledReports } from 'services/user'
import { PageTitle } from 'utils/page-title'
import { ReportsPage } from '../reports'
import { ScheduledReportRow } from './types'
import { useScheduledReports } from './use-scheduled-reports'
import { buildCreateTarget, buildManageTarget } from './routes'
import { useTrackPostHogEvent } from 'utils/posthog'
import { PostHogEvent, PostHogPath } from 'utils/posthog-events'

/**
 * Scheduled Reports v2 — playground substitute for the tabbed reports page.
 * Table = the DS Data Grid, per Figma "3. Reporting" 5770-223750 (search +
 * Filters toolbar, Refresh right, canvas header band, outline row action,
 * Pagination footer). Cells compose the DS TableCell* helpers; the vendored
 * TableCell ships `cg-max-w-0`, so wide cells opt out with `!max-w-none` (the
 * `!` beats it — twMerge can't dedupe cg- classes).
 *
 * Two tabs (DS Tabs `sm`, matching the account-overview underline pattern):
 * the live Scheduled grid and a Legacy reports tab that currently holds only a
 * deprecation Banner (the legacy table lands later).
 */

const initialsOf = (name: string) =>
	name
		.split(/[\s@.]+/)
		.slice(0, 2)
		.map((w) => w[0])
		.join('')
		.toUpperCase()

// ── Sorting ──────────────────────────────────────────────────────────────────
// Mirrors the DS TableHead `sortDirection` prop (not re-exported from the index).
type SortDirection = 'asc' | 'desc' | null
type SortKey =
	| 'name'
	| 'nextGeneration'
	| 'lastSent'
	| 'frequency'
	| 'scheduledBy'
	| 'createdAt'

// Frequency sorts by meaning, not alphabetically: cadence runs short→long.
const FREQUENCY_ORDER: Record<string, number> = {
	Daily: 0,
	Weekly: 1,
	Monthly: 2,
	Quarterly: 3
}
const collator = new Intl.Collator(undefined, { sensitivity: 'base' })
const toTime = (s: string | null) => (s ? new Date(s).getTime() : null)

const compareReports = (
	a: ScheduledReportRow,
	b: ScheduledReportRow,
	key: SortKey
): number => {
	switch (key) {
		case 'frequency':
			return FREQUENCY_ORDER[a.frequency] - FREQUENCY_ORDER[b.frequency]
		case 'nextGeneration':
		case 'createdAt':
			return new Date(a[key]).getTime() - new Date(b[key]).getTime()
		case 'lastSent': {
			const ta = toTime(a.lastSent)
			const tb = toTime(b.lastSent)
			// never-sent (null) always sinks to the bottom of an ascending sort.
			if (ta === null && tb === null) return 0
			if (ta === null) return 1
			if (tb === null) return -1
			return ta - tb
		}
		default:
			// name, scheduledBy
			return collator.compare(String(a[key] ?? ''), String(b[key] ?? ''))
	}
}

// ── Filtering ────────────────────────────────────────────────────────────────
const FREQUENCY_OPTIONS = ['Daily', 'Weekly', 'Monthly', 'Quarterly']

function toggleIn<T>(list: T[], value: T): T[] {
	return list.includes(value)
		? list.filter((x) => x !== value)
		: [...list, value]
}

const FilterSection = ({
	title,
	children
}: {
	title: string
	children: ReactNode
}) => (
	<div className="flex flex-col gap-2">
		<span className="text-xs font-semibold uppercase tracking-wide text-[rgb(var(--cg-text-tertiary))]">
			{title}
		</span>
		<div className="flex flex-col gap-1.5">{children}</div>
	</div>
)

const EmptyState = ({
	icon,
	title,
	body,
	action
}: {
	icon: ReactNode
	title: string
	body: string
	action: ReactNode
}) => (
	<div className="flex flex-col items-center justify-center gap-3 px-6 py-16 text-center">
		<div className="flex h-12 w-12 items-center justify-center rounded-full border border-[rgb(var(--cg-border))] text-[rgb(var(--cg-text-tertiary))]">
			{icon}
		</div>
		<div className="flex max-w-xs flex-col gap-1">
			<span className="text-base font-semibold text-[rgb(var(--cg-text-primary))]">
				{title}
			</span>
			<span className="text-sm text-[rgb(var(--cg-text-secondary))]">
				{body}
			</span>
		</div>
		{action}
	</div>
)

// Quick-filter KPI strip: one CardFilter per lens. These are single-select
// *views* over the list, not a partition — selecting a card narrows the table
// to that lens; the "All reports" card clears back to everything.
type KpiView = 'due' | 'mine'

const withinNextWeek = (nextGeneration: string): boolean => {
	const now = new Date()
	const startOfToday = new Date(
		now.getFullYear(),
		now.getMonth(),
		now.getDate()
	)
	const weekAhead = new Date(startOfToday)
	weekAhead.setDate(weekAhead.getDate() + 7)
	const d = new Date(nextGeneration)
	return d >= startOfToday && d <= weekAhead
}

const matchesKpiView = (
	r: ScheduledReportRow,
	view: KpiView,
	currentUserId: string | undefined
): boolean => {
	switch (view) {
		case 'due':
			return withinNextWeek(r.nextGeneration)
		case 'mine':
			return r.scheduledBy === currentUserId
	}
}

// ── Analytics-style header + pinned action column ────────────────────────────
// Mirrors the Protection Analytics dimension grid: sortable headers show a
// neutral up/down selector until active, then a single arrow (never a colour
// change); the trailing action column pins to the right edge with a soft
// edge-shadow on horizontal scroll.

// Trailing action column width. `!` is required: the DS TableCell ships
// `cg-max-w-0`, ordered after app utilities, so a plain max-w loses. 184px =
// the Manage button (~92) + gap + the kebab IconButton (28) + DS cell padding.
const ACT_W = '!w-[184px] !max-w-[184px]'

// A sticky cell paints its own opaque bg, which masks the DS row-hover (the
// pinned column would stay white while the row greys). Replay the exact DS
// hover (`cg-bg-hover` = hover-overlay at 5%) on the cell via a ::before,
// gated by the row's `group` hover, tucked between the cell bg and its content.
const STICKY_HOVER =
	"before:pointer-events-none before:absolute before:inset-0 before:-z-[1] before:bg-[rgb(var(--cg-hover-overlay)/0.05)] before:opacity-0 before:transition-opacity before:duration-150 before:content-[''] group-hover:before:opacity-100"

// The DS Table is `border-collapse: collapse`, and Chrome drops cell box-shadows
// under collapse. So the pinned cell hosts an absolutely-positioned gradient
// strip just outside its left edge; it scrolls with the cell and fades in while
// content is still hidden past the pin.
const StickyEdgeShadow = ({ show }: { show: boolean }) => (
	<span
		aria-hidden
		className={`pointer-events-none absolute inset-y-0 left-0 z-[1] w-2 -translate-x-full bg-gradient-to-l from-[rgba(0,0,0,0.10)] to-[rgba(0,0,0,0)] transition-opacity duration-200 ${
			show ? 'opacity-100' : 'opacity-0'
		}`}
	/>
)

const SortHeadCell = ({
	label,
	sortKey,
	sort,
	onSort
}: {
	label: string
	sortKey: SortKey
	sort: { key: SortKey; dir: 'asc' | 'desc' }
	onSort: (key: SortKey) => void
}) => {
	const dir: SortDirection = sort.key === sortKey ? sort.dir : null
	return (
		<TableHead>
			<button
				type="button"
				onClick={() => onSort(sortKey)}
				className="group inline-flex max-w-full items-center gap-1 text-left align-middle"
				aria-label={`Sort by ${label}${
					dir ? (dir === 'asc' ? ', ascending' : ', descending') : ''
				}`}
			>
				<span className="truncate">{label}</span>
				<span className="shrink-0 text-[rgb(var(--cg-text-tertiary))] transition-colors group-hover:text-[rgb(var(--cg-text-secondary))]">
					{dir === 'asc' ? (
						<ArrowUp className="h-3.5 w-3.5" />
					) : dir === 'desc' ? (
						<ArrowDown className="h-3.5 w-3.5" />
					) : (
						<ChevronSelectorVertical className="h-3.5 w-3.5" />
					)}
				</span>
			</button>
		</TableHead>
	)
}

const RecipientsCell = ({ recipients }: { recipients: string[] }) => {
	const shown = recipients.slice(0, 2)
	const extra = recipients.length - shown.length
	return (
		<Tooltip
			portal
			position="top"
			content={
				<div className="flex flex-col gap-0.5">
					{recipients.map((email) => (
						<span key={email}>{email}</span>
					))}
				</div>
			}
		>
			<div className="flex w-fit cursor-default items-center gap-2">
				<span className="whitespace-nowrap text-sm text-[rgb(var(--cg-text-primary))]">
					{recipients.length} recipient{recipients.length === 1 ? '' : 's'}
				</span>
				<div className="flex -space-x-1">
					{shown.map((email) => (
						<Avatar
							key={email}
							size="xxs"
							tone="purple-solid"
							initials={initialsOf(email)}
						/>
					))}
					{extra > 0 && (
						<Avatar size="xxs" tone="purple" initials={`+${extra}`} />
					)}
				</div>
			</div>
		</Tooltip>
	)
}

// Row overflow menu — the kebab holding secondary row actions (Download report,
// with more to come). The grid's viewport is `overflow-x-auto`, which would clip
// an in-flow popover, so the menu is portaled to <body> and fixed-positioned to
// the button's rect; any scroll/resize closes it so it can't detach.
// Copy for the two locked row actions (DEV-2453).
const NOT_GENERATED_TIP =
	'Not generated yet. Only generated reports are available for download.'
const NO_VIEW_ACCESS_TIP =
	"You don't have access to this report view. To access and manage it, please contact its creator and ask them to make it a shared view."

// A disabled button swallows pointer events, so the tooltip listens on a
// wrapper span and the button itself is made pointer-transparent.
const DisabledWithTip = ({
	tip,
	children
}: {
	tip: string
	children: ReactNode
}) => (
	<Tooltip content={tip} position="top" portal maxWidth={280}>
		<span className="inline-flex [&>button]:pointer-events-none">
			{children}
		</span>
	</Tooltip>
)

const RowActionsMenu = ({
	onDownload,
	downloadDisabled,
	onStop
}: {
	onDownload: () => void
	/** No generation exists yet — Download is shown but locked (DEV-2453). */
	downloadDisabled: boolean
	onStop: () => void
}) => {
	const [open, setOpen] = useState(false)
	const btnRef = useRef<HTMLButtonElement | HTMLAnchorElement>(null)
	const menuRef = useRef<HTMLDivElement>(null)
	const [pos, setPos] = useState<{ top: number; right: number }>({
		top: 0,
		right: 0
	})

	// Two rows (~88px + padding). Open upward when the button sits too close to
	// the bottom of the viewport — otherwise the second item (Stop scheduling)
	// renders below the fold and "disappears" for the last rows of a page.
	const MENU_H = 104
	const place = useCallback(() => {
		const el = btnRef.current
		if (!el) return
		const r = el.getBoundingClientRect()
		const below = r.bottom + 4 + MENU_H <= window.innerHeight
		setPos({
			top: below ? r.bottom + 4 : Math.max(8, r.top - 4 - MENU_H),
			right: window.innerWidth - r.right
		})
	}, [])

	useLayoutEffect(() => {
		if (open) place()
	}, [open, place])

	useEffect(() => {
		if (!open) return
		const onDown = (e: MouseEvent) => {
			const t = e.target as Node
			if (btnRef.current?.contains(t) || menuRef.current?.contains(t)) return
			setOpen(false)
		}
		const close = () => setOpen(false)
		document.addEventListener('mousedown', onDown)
		window.addEventListener('resize', close)
		// capture-phase so an inner scroll container closes it too.
		window.addEventListener('scroll', close, true)
		return () => {
			document.removeEventListener('mousedown', onDown)
			window.removeEventListener('resize', close)
			window.removeEventListener('scroll', close, true)
		}
	}, [open])

	return (
		<>
			{/* `md` (32×32) so the kebab matches the sm Manage button's 32px height;
			    IconButton is the canonical icon-only ghost button — transparent at
			    rest, cg-bg-hover on hover. */}
			<IconButton
				ref={btnRef}
				size="md"
				color="gray"
				aria-label="More actions"
				icon={<DotsVertical />}
				onClick={() => setOpen((o) => !o)}
			/>
			{open &&
				createPortal(
					<div
						ref={menuRef}
						style={{
							position: 'fixed',
							top: pos.top,
							right: pos.right,
							zIndex: 60
						}}
					>
						<MenuList style={{ width: 220 }}>
							{downloadDisabled ? (
								<Tooltip
									content={NOT_GENERATED_TIP}
									position="left"
									portal
									maxWidth={260}
								>
									<div className="[&>button]:pointer-events-none">
										<ListItem
											label="Download report"
											leadingIcon={<Download01 className="h-4 w-4" />}
											disabled
										/>
									</div>
								</Tooltip>
							) : (
								<ListItem
									label="Download report"
									leadingIcon={<Download01 className="h-4 w-4" />}
									onClick={() => {
										onDownload()
										setOpen(false)
									}}
								/>
							)}
							{/* Same action as the Share drawer's "Stop scheduling": deletes the
							    schedule row (§6.9). Available whether or not the caller can open
							    the view, so an orphaned schedule can always be stopped. */}
							<ListItem
								label="Stop scheduling"
								variant="destructive"
								leadingIcon={<Trash01 className="h-4 w-4" />}
								onClick={() => {
									onStop()
									setOpen(false)
								}}
							/>
						</MenuList>
					</div>,
					document.body
				)}
		</>
	)
}

const ReportRow = ({
	report,
	onManage,
	onDownload,
	onStop,
	scrolled
}: {
	report: ScheduledReportRow
	onManage: (report: ScheduledReportRow) => void
	onDownload: (report: ScheduledReportRow) => void
	onStop: (report: ScheduledReportRow) => void
	/** True while the grid has content hidden past the pinned action column. */
	scrolled: boolean
}) => (
	<TableRow className="group">
		<TableCell className="!max-w-none">
			<TableCellText primary={report.name} />
		</TableCell>
		<TableCell className="!max-w-none whitespace-nowrap">
			{report.nextGeneration}
		</TableCell>
		<TableCell className="!max-w-none whitespace-nowrap text-[rgb(var(--cg-text-secondary))]">
			{report.lastSent ?? '—'}
		</TableCell>
		<TableCell className="!max-w-none">{report.frequency}</TableCell>
		<TableCell className="!max-w-none whitespace-nowrap">
			{report.period}
		</TableCell>
		<TableCell className="!max-w-none">
			<RecipientsCell recipients={report.recipients} />
		</TableCell>
		<TableCell className="!max-w-none whitespace-nowrap">
			{report.scheduledByName}
		</TableCell>
		<TableCell className="!max-w-none whitespace-nowrap">
			{report.createdAt}
		</TableCell>
		<TableCell
			className={`sticky right-0 z-[1] bg-[rgb(var(--cg-surface))] ${STICKY_HOVER} ${ACT_W}`}
		>
			<StickyEdgeShadow show={scrolled} />
			<div className="flex items-center justify-end gap-1">
				{report.viewAccessible ? (
					<AppButton
						variant="outline"
						size="sm"
						label="Manage"
						rightIcon={<ChevronRight />}
						onClick={() => onManage(report)}
					/>
				) : (
					// The template is another user's private view: Manage can't open it
					// (DEV-2453). Download / Stop in the ••• still work.
					<DisabledWithTip tip={NO_VIEW_ACCESS_TIP}>
						<AppButton
							variant="outline"
							size="sm"
							label="Manage"
							rightIcon={<ChevronRight />}
							disabled
						/>
					</DisabledWithTip>
				)}
				<RowActionsMenu
					onDownload={() => onDownload(report)}
					downloadDisabled={!report.generated}
					onStop={() => onStop(report)}
				/>
			</div>
		</TableCell>
	</TableRow>
)

// Legacy reports tab: a deprecation notice on top, then the full existing
// legacy reports experience embedded below it (Available / Recent / Downloads /
// legacy Scheduled). Warning tone reads as "heads up, this is going away".
const LEGACY_SUNSET = 'December 31, 2026'
const LegacyPanel = () => (
	<div className="flex flex-col gap-4">
		<Banner
			tone="warning"
			// Banner ships cg-rounded-2xl (16px); DS styles.css loads after the app
			// css so !important is needed to pin Esteban's staged 12px edit.
			className="!rounded-xl"
			title="Legacy reports are being retired"
			description={
				<span>
					The scheduled reports experience has been redesigned to give you
					greater flexibility and higher{'‑'}quality insights into your traffic
					quality. Legacy report templates on this page will be retired on{' '}
					<span className="font-bold">{LEGACY_SUNSET}</span>; please configure
					the reports you need in the Analytics page before that date. The new
					scheduled reports appear under{' '}
					<span className="font-bold">Scheduled Reports {'>'} Scheduled</span>
				</span>
			}
		/>
		<ReportsPage />
	</div>
)

export const ScheduledReportsPage = () => {
	const { accountId, assetId } = useParams<{
		accountId: string
		assetId: string
	}>()
	const { rows: liveRows, isLoading, isError, refetch } = useScheduledReports()
	const currentUserId = useUserStore((s) => s.user?.id)
	// Creator id -> display name, sourced from the live rows (the hook already
	// resolved each name from the account members). Drives the "Scheduled by"
	// filter labels while the filter still matches on the stable id.
	const ownerNameById = useMemo(() => {
		const m: Record<string, string> = {}
		for (const r of liveRows) m[r.scheduledBy] = r.scheduledByName
		return m
	}, [liveRows])
	const domain = useAssetStore(
		(state) => state.asset?.domain ?? 'demo.example.com'
	)
	const trackEvent = useTrackPostHogEvent()
	// Manage opens the schedule's OWN view with the schedule drawer; New report
	// opens the "name this view" form (a report has to exist before it can be
	// scheduled).
	const handleManage = (report: ScheduledReportRow) =>
		accountId &&
		assetId &&
		window.location.assign(buildManageTarget(accountId, assetId, report.viewId))
	const handleCreate = () =>
		accountId &&
		assetId &&
		window.location.assign(buildCreateTarget(accountId, assetId))
	// Open the report's last generation (§6.8). The row's Download is locked until
	// one exists (DEV-2453), so a miss here is a race, not the normal path.
	const handleDownload = async (report: ScheduledReportRow) => {
		if (!accountId || !assetId || !report.generated) return
		try {
			const { url } = await ScheduledReports.downloadLast(
				accountId,
				assetId,
				report.id
			)
			window.open(url, '_blank')
			trackEvent(PostHogEvent.DOWNLOAD, PostHogPath.SCHEDULED_REPORTS_TAB, null)
		} catch {
			toast.error('This report has no generated file to download yet.')
		}
	}
	// "Stop scheduling" from the row's ••• — the same delete as the Share drawer's
	// footer action (§6.9, no restore), behind the same confirmation.
	const [stopping, setStopping] = useState<ScheduledReportRow | null>(null)
	const handleStopConfirmed = async () => {
		const target = stopping
		if (!target || !accountId || !assetId) return
		try {
			await ScheduledReports.stop(accountId, assetId, target.id)
			setStopping(null)
			await refetch()
			toast.success('Report scheduling stopped.')
		} catch {
			toast.error('Could not stop the schedule. Please try again.')
		}
	}
	const ownerOptions = useMemo(
		() =>
			Array.from(new Set(liveRows.map((r) => r.scheduledBy))).sort((a, b) =>
				collator.compare(ownerNameById[a] ?? a, ownerNameById[b] ?? b)
			),
		[liveRows, ownerNameById]
	)

	const [tab, setTab] = useState('scheduled')
	const [search, setSearch] = useState('')
	const [kpiView, setKpiView] = useState<KpiView | null>(null)
	const [freqFilter, setFreqFilter] = useState<string[]>([])
	const [ownerFilter, setOwnerFilter] = useState<string[]>([])
	const [sort, setSort] = useState<{ key: SortKey; dir: 'asc' | 'desc' }>({
		key: 'nextGeneration',
		dir: 'asc'
	})
	const [filtersOpen, setFiltersOpen] = useState(false)
	const [page, setPage] = useState(1)
	const [pageSize, setPageSize] = useState(10)

	const activeFilterCount = freqFilter.length + ownerFilter.length

	// Monitoring (§Monitoring): pageview per tab — `pageview` on Scheduled,
	// `pageview_legacy` on Legacy reports. Fires on landing and tab switch.
	useEffect(() => {
		trackEvent(
			tab === 'legacy' ? PostHogEvent.PAGEVIEW_LEGACY : PostHogEvent.PAGEVIEW,
			PostHogPath.SCHEDULED_REPORTS_TAB,
			null
		)
	}, [tab, trackEvent])

	// Close the filter popover on outside click.
	const filterRef = useRef<HTMLDivElement>(null)
	useEffect(() => {
		if (!filtersOpen) return
		const onDown = (e: MouseEvent) => {
			if (filterRef.current && !filterRef.current.contains(e.target as Node))
				setFiltersOpen(false)
		}
		document.addEventListener('mousedown', onDown)
		return () => document.removeEventListener('mousedown', onDown)
	}, [filtersOpen])

	// Search + facet filters, then sort. Runs over every report; pagination
	// slices the result below.
	const processed = useMemo(() => {
		const q = search.trim().toLowerCase()
		const out = liveRows.filter((r) => {
			if (kpiView && !matchesKpiView(r, kpiView, currentUserId)) return false
			if (
				q &&
				!r.name.toLowerCase().includes(q) &&
				!r.scheduledByName.toLowerCase().includes(q)
			)
				return false
			if (freqFilter.length && !freqFilter.includes(r.frequency)) return false
			if (ownerFilter.length && !ownerFilter.includes(r.scheduledBy))
				return false
			return true
		})
		out.sort((a, b) => {
			const r = compareReports(a, b, sort.key)
			return sort.dir === 'asc' ? r : -r
		})
		return out
	}, [liveRows, search, kpiView, freqFilter, ownerFilter, sort, currentUserId])

	const totalPages = Math.max(1, Math.ceil(processed.length / pageSize))
	const currentPage = Math.min(page, totalPages)
	const rows = processed.slice(
		(currentPage - 1) * pageSize,
		currentPage * pageSize
	)

	// Pin-column scroll state: cast the action column's edge shadow only while the
	// grid has content hidden past the pinned right edge. Measured off the scroll
	// viewport, re-run on scroll, resize, and row changes.
	const scrollRef = useRef<HTMLDivElement | null>(null)
	const [edge, setEdge] = useState<{ start: boolean; end: boolean }>({
		start: true,
		end: true
	})
	const measure = useCallback(() => {
		const el = scrollRef.current
		if (!el) return
		const start = el.scrollLeft <= 0
		const end = el.scrollLeft + el.clientWidth >= el.scrollWidth - 1
		setEdge((p) => (p.start === start && p.end === end ? p : { start, end }))
	}, [])
	useLayoutEffect(() => {
		measure()
	}, [measure, rows])
	useEffect(() => {
		const el = scrollRef.current
		if (!el || typeof ResizeObserver === 'undefined') return
		const ro = new ResizeObserver(() => measure())
		ro.observe(el)
		return () => ro.disconnect()
	}, [measure])

	const noData = liveRows.length === 0
	const noMatches = !noData && processed.length === 0

	const onSort = (key: SortKey) =>
		setSort((s) =>
			s.key === key
				? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' }
				: { key, dir: 'asc' }
		)
	const resetFilterSelections = () => {
		setFreqFilter([])
		setOwnerFilter([])
		setPage(1)
	}
	// Toggle a KPI quick-view: click the active card again to clear it.
	const toggleKpi = (view: KpiView) => {
		setKpiView((v) => (v === view ? null : view))
		setPage(1)
	}
	// The "All reports" card is the reset — it drops back to every report.
	const showAll = () => {
		setKpiView(null)
		setPage(1)
	}
	// The no-matches state clears every filter — search, KPI view, and facets —
	// since any of them could be hiding rows.
	const clearAll = () => {
		setSearch('')
		setKpiView(null)
		resetFilterSelections()
	}

	// Account-wide reporting summary — computed over every report, not the search
	// result, since these answer "what do I have?" not "how many match".
	const summary = useMemo(() => {
		let dueThisWeek = 0
		let mine = 0
		for (const r of liveRows) {
			if (withinNextWeek(r.nextGeneration)) dueThisWeek += 1
			if (r.scheduledBy === currentUserId) mine += 1
		}
		return { total: liveRows.length, dueThisWeek, mine }
	}, [liveRows, currentUserId])

	return (
		<>
			<PageTitle pageName="Scheduled Reports" entityName={domain} />
			<PageLayout
				title="Scheduled Reports"
				subtitle={`Automated reports scheduled for ${domain}`}
			>
				<div className="pb-24">
					{/* Underline tabs (DS Tabs `sm`, account-overview pattern): the live
					    grid + a Legacy reports tab that currently holds only the
					    deprecation banner. */}
					<Tabs size="sm" value={tab} onChange={setTab}>
						<TabList fullBleed className="mb-4">
							<Tab value="scheduled" label="Scheduled" />
							<Tab value="legacy" label="Legacy reports" />
						</TabList>

						<TabPanel value="scheduled">
							{/* Operational summary as a quick-filter strip — the canonical DS
							    CardFilter (Figma "Card Filter", Reporting 5526:215069):
							    pressable KPI tiles that filter the table one lens at a time,
							    with a brand underline on the active card. "All reports" is the
							    resting/reset card, selected whenever no lens is active. */}
							<div className="mb-4 flex overflow-hidden rounded-xl border border-[rgb(var(--cg-border))]">
								<CardFilter
									className="flex-1"
									icon={<Rows01 />}
									label="All reports"
									value={summary.total}
									selected={kpiView === null}
									onSelectedChange={showAll}
								/>
								<CardFilter
									className="flex-1 border-l border-[rgb(var(--cg-border))]"
									icon={<Clock />}
									label="Due this week"
									value={summary.dueThisWeek}
									selected={kpiView === 'due'}
									onSelectedChange={() => toggleKpi('due')}
								/>
								<CardFilter
									className="flex-1 border-l border-[rgb(var(--cg-border))]"
									icon={<User01 />}
									label="Scheduled by me"
									value={summary.mine}
									selected={kpiView === 'mine'}
									onSelectedChange={() => toggleKpi('mine')}
								/>
							</div>
							<div className="rounded-xl border border-[rgb(var(--cg-border))] bg-[rgb(var(--cg-surface))] p-3">
								{isLoading ? (
									<div className="flex items-center justify-center py-16">
										<DotMatrix size="md" label="Loading scheduled reports" />
									</div>
								) : isError ? (
									<EmptyState
										icon={<CalendarPlus02 className="h-6 w-6" />}
										title="Couldn't load scheduled reports"
										body="Something went wrong loading your scheduled reports. Please try again."
										action={
											<AppButton
												variant="secondary"
												size="sm"
												label="Retry"
												onClick={() => refetch()}
											/>
										}
									/>
								) : noData ? (
									<EmptyState
										icon={<CalendarPlus02 className="h-6 w-6" />}
										title="No scheduled reports yet"
										body="Create a report to have it generated and emailed to your team automatically."
										action={
											<AppButton
												variant="primary"
												size="sm"
												label="New report"
												leftIcon={<Plus />}
												onClick={handleCreate}
											/>
										}
									/>
								) : (
									<>
										<div className="flex items-center justify-between gap-3">
											<div className="flex items-center gap-3">
												{/* [&_span]: pulls the DS leading-icon span's cg-pl-3.5 (14px)
												    back to 10px; !border lightens the wrapper's border-strong
												    to match the toolbar buttons (twMerge can't dedupe cg-). */}
												<div className="w-[180px] [&_span[aria-hidden]]:!pl-2.5">
													<Input
														state="default"
														size="sm"
														className="[&:not(:focus-within)]:!border-[rgb(var(--cg-border))]"
														leadingIcon={<SearchLg className="h-4 w-4" />}
														// 6px vertical padding lands the field at 32px (18px
														// text line + 2px wrapper border), flush with the sm
														// AppButtons — the DS sm size's py-2 renders 36px.
														style={{ paddingTop: 6, paddingBottom: 6 }}
														placeholder="Search for reports..."
														value={search}
														onChange={(e) => {
															setSearch(e.target.value)
															setPage(1)
														}}
													/>
												</div>
												{/* `flex` so this popover-anchor wrapper hugs the 32px button
											    instead of adding a text line-box (which would sit the button
											    ~1px high and misalign it from the search field). */}
												<div ref={filterRef} className="relative flex">
													<AppButton
														variant="secondary"
														size="sm"
														label={
															activeFilterCount > 0
																? `Filters · ${activeFilterCount}`
																: 'Filters'
														}
														leftIcon={<FilterLines />}
														onClick={() => setFiltersOpen((o) => !o)}
													/>
													{filtersOpen && (
														<div className="absolute left-0 top-[calc(100%+6px)] z-20 w-64 rounded-xl border border-[rgb(var(--cg-border))] bg-[rgb(var(--cg-surface))] p-3 shadow-lg">
															<div className="flex flex-col gap-3">
																<FilterSection title="Frequency">
																	{FREQUENCY_OPTIONS.map((f) => (
																		<Checkbox
																			key={f}
																			size="sm"
																			label={f}
																			checked={freqFilter.includes(f)}
																			onChange={() => {
																				setFreqFilter((s) => toggleIn(s, f))
																				setPage(1)
																			}}
																		/>
																	))}
																</FilterSection>
																<FilterSection title="Scheduled by">
																	<div className="flex max-h-40 flex-col gap-1.5 overflow-y-auto">
																		{ownerOptions.map((owner) => (
																			<Checkbox
																				key={owner}
																				size="sm"
																				label={ownerNameById[owner] ?? owner}
																				checked={ownerFilter.includes(owner)}
																				onChange={() => {
																					setOwnerFilter((s) =>
																						toggleIn(s, owner)
																					)
																					setPage(1)
																				}}
																			/>
																		))}
																	</div>
																</FilterSection>
															</div>
															<div className="mt-3 flex items-center justify-between border-t border-[rgb(var(--cg-border))] pt-3">
																<button
																	type="button"
																	onClick={resetFilterSelections}
																	disabled={activeFilterCount === 0}
																	className="text-sm text-[rgb(var(--cg-text-link))] disabled:cursor-default disabled:text-[rgb(var(--cg-text-disabled))]"
																>
																	Clear all
																</button>
																<AppButton
																	variant="secondary"
																	size="sm"
																	label="Done"
																	onClick={() => setFiltersOpen(false)}
																/>
															</div>
														</div>
													)}
												</div>
											</div>
											<div className="flex items-center gap-3">
												<AppButton
													variant="secondary"
													size="sm"
													label="Refresh"
													leftIcon={<RefreshCw02 />}
												/>
												<AppButton
													variant="primary"
													size="sm"
													label="New report"
													leftIcon={<Plus />}
													onClick={handleCreate}
												/>
											</div>
										</div>
										{noMatches ? (
											<EmptyState
												icon={<FileSearch02 className="h-6 w-6" />}
												title="No reports match"
												body="Try a different search, or clear your filters to see every scheduled report."
												action={
													<AppButton
														variant="secondary"
														size="sm"
														label="Clear filters"
														leftIcon={<XClose />}
														onClick={clearAll}
													/>
												}
											/>
										) : (
											<>
												<div
													ref={scrollRef}
													onScroll={measure}
													className="mt-3 overflow-x-auto overflow-y-hidden rounded-lg"
												>
													{/* Header type is set explicitly: the DS TableHead uses text-xs,
													    which is 10px in this app's Tailwind scale. Matches the PA
													    dimension grid at 12px / medium. */}
													<Table className="[&_th]:!text-[12px] [&_th]:!font-medium">
														<TableHeader className="cg-bg-canvas">
															<TableRow className="hover:!bg-transparent">
																<SortHeadCell
																	label="Report name"
																	sortKey="name"
																	sort={sort}
																	onSort={onSort}
																/>
																<SortHeadCell
																	label="Next generation"
																	sortKey="nextGeneration"
																	sort={sort}
																	onSort={onSort}
																/>
																<SortHeadCell
																	label="Last sent"
																	sortKey="lastSent"
																	sort={sort}
																	onSort={onSort}
																/>
																<SortHeadCell
																	label="Frequency"
																	sortKey="frequency"
																	sort={sort}
																	onSort={onSort}
																/>
																<TableHead>Period</TableHead>
																<TableHead>Recipients</TableHead>
																<SortHeadCell
																	label="Scheduled by"
																	sortKey="scheduledBy"
																	sort={sort}
																	onSort={onSort}
																/>
																<SortHeadCell
																	label="Created at"
																	sortKey="createdAt"
																	sort={sort}
																	onSort={onSort}
																/>
																<TableHead
																	aria-label="Actions"
																	className={`sticky right-0 z-[2] cg-bg-canvas ${ACT_W}`}
																>
																	<StickyEdgeShadow show={!edge.end} />
																</TableHead>
															</TableRow>
														</TableHeader>
														<TableBody>
															{rows.map((report) => (
																<ReportRow
																	key={report.id}
																	report={report}
																	onManage={handleManage}
																	onDownload={handleDownload}
																	onStop={setStopping}
																	scrolled={!edge.end}
																/>
															))}
														</TableBody>
													</Table>
												</div>
												<Pagination
													page={currentPage}
													totalPages={totalPages}
													onPageChange={setPage}
													pageSize={pageSize}
													pageSizeOptions={[5, 10, 25]}
													onPageSizeChange={(size) => {
														setPageSize(size)
														setPage(1)
													}}
												/>
											</>
										)}
									</>
								)}
							</div>
						</TabPanel>

						<TabPanel value="legacy">
							<LegacyPanel />
						</TabPanel>
					</Tabs>
				</div>
			</PageLayout>
			{/* Stop-scheduling confirmation — same dialog and copy as the Share
			    drawer's footer action, so the two entry points read identically. */}
			<ConfirmDialog
				open={!!stopping}
				onClose={() => setStopping(null)}
				onConfirm={handleStopConfirmed}
				tone="danger"
				title="Stop scheduling?"
				description={`“${
					stopping?.name ?? 'This report'
				}” will stop being sent. You can schedule it again anytime.`}
				confirmLabel="Stop scheduling"
				cancelLabel="Keep schedule"
			/>
		</>
	)
}
